<script>
import { mapGetters } from 'vuex';
import ExecutionDetail from './ExecutionDetail.vue';

export default {
  name: 'ExecutionLogDrawer',
  components: { ExecutionDetail },

  props: {
    ruleId: { type: Number, required: true },
    ruleType: { type: String, default: 'DealAutomationRule' },
    ruleName: { type: String, default: '' },
  },

  data() {
    return {
      isOpen: false,
      currentPage: 1,
      filters: { status: '' },
      selectedExecution: null,
    };
  },

  computed: {
    ...mapGetters({
      executions: 'automationExecutions/getExecutions',
      meta: 'automationExecutions/getMeta',
      uiFlags: 'automationExecutions/getUIFlags',
    }),

    isFetching() {
      return this.uiFlags?.isFetching || false;
    },

    accountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },

  methods: {
    open() {
      this.isOpen = true;
      this.currentPage = 1;
      this.loadExecutions();
    },

    close() {
      this.isOpen = false;
      this.selectedExecution = null;
    },

    async loadExecutions() {
      await this.$store.dispatch('automationExecutions/index', {
        accountId: this.accountId,
        params: {
          rule_id: this.ruleId,
          rule_type: this.ruleType,
          status: this.filters.status || undefined,
          page: this.currentPage,
          per_page: 20,
        },
      });
    },

    changePage(page) {
      this.currentPage = page;
      this.loadExecutions();
    },

    async openDetail(execution) {
      await this.$store.dispatch('automationExecutions/show', {
        accountId: this.accountId,
        id: execution.id,
      });
      this.selectedExecution =
        this.$store.getters['automationExecutions/getCurrentExecution'];
    },

    statusIcon(status) {
      const icons = {
        success: 'i-lucide-check-circle',
        failed: 'i-lucide-x-circle',
        skipped: 'i-lucide-minus-circle',
        partial: 'i-lucide-alert-circle',
      };
      return icons[status] || 'i-lucide-circle';
    },

    formatEvent(event) {
      const labels = {
        deal_created: 'Negócio criado',
        deal_updated: 'Negócio atualizado',
        deal_stage_changed: 'Stage alterado',
        deal_won: 'Negócio ganho',
        deal_lost: 'Negócio perdido',
        deal_assignee_changed: 'Responsável alterado',
      };
      return labels[event] || event;
    },

    formatTime(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable-next-line vue/no-root-v-if -->
  <div v-if="isOpen" class="execution-log-drawer">
    <div class="drawer-backdrop" @click="close" />
    <div class="drawer-panel">
      <div class="drawer-header">
        <div class="drawer-title">
          <span class="rule-name">{{ ruleName }}</span>
          <span class="drawer-subtitle">{{
            $t('AUTOMATION.EXECUTION_LOG.TITLE')
          }}</span>
        </div>
        <woot-button
          variant="ghost"
          color="slate"
          icon="i-lucide-x"
          size="sm"
          @click="close"
        />
      </div>

      <div class="drawer-filters">
        <select
          v-model="filters.status"
          class="status-filter"
          @change="loadExecutions"
        >
          <option value="">
            {{ $t('AUTOMATION.EXECUTION_LOG.ALL_STATUSES') }}
          </option>
          <option value="success">
            {{ $t('AUTOMATION.EXECUTION_LOG.STATUS.SUCCESS') }}
          </option>
          <option value="failed">
            {{ $t('AUTOMATION.EXECUTION_LOG.STATUS.FAILED') }}
          </option>
          <option value="skipped">
            {{ $t('AUTOMATION.EXECUTION_LOG.STATUS.SKIPPED') }}
          </option>
          <option value="partial">
            {{ $t('AUTOMATION.EXECUTION_LOG.STATUS.PARTIAL') }}
          </option>
        </select>
      </div>

      <div class="drawer-body">
        <div v-if="isFetching" class="loading-state">
          <span class="i-lucide-loader-circle animate-spin" />
        </div>

        <div v-else-if="executions.length === 0" class="empty-state">
          <span class="i-lucide-history empty-icon" />
          <p>{{ $t('AUTOMATION.EXECUTION_LOG.EMPTY') }}</p>
        </div>

        <div v-else class="execution-list">
          <div
            v-for="execution in executions"
            :key="execution.id"
            class="execution-item"
            @click="openDetail(execution)"
          >
            <div
              class="execution-status-badge"
              :class="`status-${execution.status}`"
            >
              <span :class="statusIcon(execution.status)" />
            </div>
            <div class="execution-info">
              <span class="execution-event">{{
                formatEvent(execution.trigger_event)
              }}</span>
              <span class="execution-time">{{
                formatTime(execution.executed_at)
              }}</span>
            </div>
            <div class="execution-meta">
              <span v-if="execution.duration_ms" class="execution-duration">
                {{ execution.duration_ms }}ms
              </span>
              <span class="i-lucide-chevron-right" />
            </div>
          </div>
        </div>

        <div v-if="meta.totalPages > 1" class="pagination">
          <woot-button
            variant="ghost"
            color="slate"
            size="xs"
            :disabled="currentPage <= 1 || isFetching"
            icon="i-lucide-chevron-left"
            @click="changePage(currentPage - 1)"
          />
          <span class="page-info"
            >{{ currentPage }} / {{ meta.totalPages }}</span
          >
          <woot-button
            variant="ghost"
            color="slate"
            size="xs"
            :disabled="currentPage >= meta.totalPages || isFetching"
            icon="i-lucide-chevron-right"
            @click="changePage(currentPage + 1)"
          />
        </div>
      </div>
    </div>

    <ExecutionDetail
      v-if="selectedExecution"
      :execution="selectedExecution"
      @close="selectedExecution = null"
    />
  </div>
</template>

<style scoped>
.execution-log-drawer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.drawer-backdrop {
  flex: 1;
  background: rgba(0, 0, 0, 0.3);
}

.drawer-panel {
  width: 400px;
  background: white;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.drawer-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rule-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.drawer-filters {
  padding: 12px 20px;
  border-bottom: 1px solid #f3f4f6;
}

.status-filter {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: white;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #9ca3af;
  gap: 12px;
}

.empty-icon {
  font-size: 40px;
  opacity: 0.4;
}

.execution-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.15s;
}

.execution-item:hover {
  background: #f9fafb;
}

.execution-status-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.status-success {
  background: #dcfce7;
  color: #16a34a;
}
.status-failed {
  background: #fee2e2;
  color: #dc2626;
}
.status-skipped {
  background: #f3f4f6;
  color: #9ca3af;
}
.status-partial {
  background: #fef9c3;
  color: #ca8a04;
}

.execution-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.execution-event {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.execution-time {
  font-size: 11px;
  color: #9ca3af;
}

.execution-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #9ca3af;
  font-size: 11px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #f3f4f6;
}

.page-info {
  font-size: 13px;
  color: #6b7280;
}
</style>
